import { Injectable } from '@angular/core'
import { environment } from '@employer/environments/environment'
import { RestApiClient } from '@engineering11/web-api-rest'
import { BehaviorSubject, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { DEFAULT_NETWORK_MONITORING_CONFIG, getBaseSuffix, TokenStorage } from 'shared-lib'
import { AssistantPosture, parsePosture } from '../models/assistant-posture.model'
import { TourEngineService } from './tour-engine.service'

// BACKEND CONTRACT (assumption) — see ../BACKEND-CONTRACT.md
// GAP (2026-08-12): service-assistant exposes NO GET for user-state, so there is no stored-posture read
// endpoint. Left pointed at the natural resource path as a placeholder; it 404s until the backend adds a
// GET v1/user-state route. Posture still resolves from the toggle/tour-completion responses below.
const POSTURE_READ_PATH = 'user-state'

// Mode-toggle (POST) under `environment.services.assistant`. Reconciled 2026-08-12: the backend route is a
// pure reverse — POST v1/user-state/mode/toggle with NO body; the response is the IAssistantUserState doc
// whose `mode` field carries the stored posture.
const POSTURE_TOGGLE_PATH = 'user-state/mode/toggle'

// Tour-completion (POST). Reconciled 2026-08-12: POST v1/user-state/tour-completion, body { outcome },
// response IAssistantUserState (`mode` field).
const TOUR_COMPLETION_PATH = 'user-state/tour-completion'

/** Internal sentinel: an endpoint error resolves to "retain the prior posture" rather than crash (CAP-3). */
const KEEP = '__keep__' as const

/**
 * Owns the server-authoritative assistant posture (`posture$`). Built on the same `RestApiClient` +
 * `getBaseSuffix` construction as `assistant-conversation.service.ts` (`.get`/`.post` return
 * `Observable<AxiosResponse<Res>>`, so bodies are read via `res.data`).
 *
 * `posture$` is only ever advanced from a backend-sourced value — the load read, the mode-toggle
 * response (or a re-read when the response omits it), or the tour-completion response (defaulting to
 * `Helper`, the documented completion semantics). There is never an optimistic local flip, and any
 * endpoint error leaves the prior posture intact.
 *
 * The posture→tour-engine dependency is one-way: this service subscribes to `tourEngine.tourTerminated$`
 * to record completion — the engine imports/knows nothing about this service, so no cycle exists and the
 * tour's UI-only walk is preserved.
 */
@Injectable({ providedIn: 'root' })
export class AssistantPostureService {
  private restClient: RestApiClient

  /** Server-authoritative posture; `undefined` until the first backend value arrives (no hardcoded default). */
  readonly posture$ = new BehaviorSubject<AssistantPosture | undefined>(undefined)

  constructor(tokenStorage: TokenStorage, tourEngine: TourEngineService) {
    this.restClient = new RestApiClient({
      // Reconciled 2026-08-12: assistant is its own service (service-assistant at /assistant).
      baseUrl: environment.services.assistant,
      token: () => tokenStorage.getAccessToken(),
      baseSuffix: getBaseSuffix(environment, 'assistant'),
      ...DEFAULT_NETWORK_MONITORING_CONFIG,
    })

    // A terminal tour resolution (complete/skip) records completion and reflects the server's
    // guide→helper flip. Subscribing here (not inside the engine) keeps the engine UI-only.
    tourEngine.tourTerminated$.subscribe(reason => this.recordTourCompletion(reason))
  }

  /** Read the stored posture and reflect it. On any error the prior posture is retained (no crash). */
  loadPosture(): void {
    this.restClient
      // Reconciled 2026-08-12: stored posture lives on the user-state doc's `mode` field.
      .get<{ mode?: string }>(POSTURE_READ_PATH)
      .pipe(
        map(res => parsePosture(res.data?.mode)),
        catchError(() => of(KEEP))
      )
      .subscribe(next => {
        if (next === KEEP) return // error → retain prior posture
        if (next) this.posture$.next(next) // reflect the stored posture; ignore an unparseable body
      })
  }

  /**
   * Switch to `target` via the mode-toggle endpoint, then reflect the stored posture the backend returns.
   * If the response omits a valid posture, re-read via `loadPosture()` (still server-authoritative). On
   * error the prior posture is retained.
   */
  setPosture(target: AssistantPosture): void {
    this.restClient
      // Reconciled 2026-08-12: the backend toggle is a pure reverse (no body); `target` is unused now but
      // kept in the signature so the caller's intent stays explicit. Response `mode` carries the stored posture.
      .post<{ mode?: string }>(POSTURE_TOGGLE_PATH, {})
      .pipe(
        map(res => parsePosture(res.data?.mode)),
        catchError(() => of(KEEP))
      )
      .subscribe(next => {
        if (next === KEEP) return // error → retain prior posture
        if (next) this.posture$.next(next) // reflect the stored posture
        else this.loadPosture() // response missing a valid posture → re-read (still server-authoritative)
      })
  }

  /**
   * Record a terminal tour resolution and reflect the resulting posture. A response posture is honored;
   * a success body without a valid posture defaults to `Helper` (the documented completion semantics —
   * guide→helper). On error the prior posture is retained.
   */
  recordTourCompletion(reason: 'completed' | 'skipped'): void {
    this.restClient
      // Reconciled 2026-08-12: body field is `outcome`; response `mode` carries the stored posture.
      .post<{ mode?: string }>(TOUR_COMPLETION_PATH, { outcome: reason })
      .pipe(
        map(res => parsePosture(res.data?.mode)),
        catchError(() => of(KEEP))
      )
      .subscribe(next => {
        if (next === KEEP) return // error → retain prior posture
        this.posture$.next(next ?? AssistantPosture.Helper) // completion defaults to Helper
      })
  }
}
